import {
  checkoutStarted,
  MerchProductOrderItem,
  orderCompleted,
} from '@webapp/utils/rudderstack/events';
import SimpleArtist from '@common/types/artist/simple-artist';
import { MerchSale } from '@services/merch/types/api/merch-sale';
import { Order } from '@webapp/types/orders/order';
import User from '@common/types/user/user';
import { Subscription } from '@services/subscription/types/subscription';
import { CreateSubscriptionSuccess } from '@webapp/store/subscription/action-creators';
import DineroFactory from 'dinero.js';
import { PaymentIntent } from '@stripe/stripe-js';

export const checkoutStartedMerchEvent = async ({
  artist,
  order,
}: {
  artist?: SimpleArtist;
  order: MerchSale;
}) => {
  await checkoutStarted({
    created_by: 'Artist',
    artist_id: artist?.id,
    artist_name: artist?.name,
    checkout_source: 'merch release',
    currency: 'USD',
    products: order?.products?.map((product) => ({
      product_id: product.id,
      sku: product.sku,
      name: product.title,
      size: product.size.name,
      color: product.color?.name,
      variant: product.variantId,
      price: product.price,
      quantity: product.quantity,
    })),
  });
};

export const checkoutStartedLabelUpgradeEvent = async ({
  user,
  order,
}: {
  user?: Partial<User>;
  order: Order;
}) => {
  await checkoutStarted({
    created_by: 'User',
    user_id: user?.id,
    user_name: user?.name,
    checkout_source: 'artist label upgrade',
    currency: 'USD',
    products: [
      {
        name: 'Distribution Pro',
        category: 'Subscription',
        variant: 'yearly',
        product_id: order.product.externalId || order.product.id,
        position: 1,
        price: order.price.value,
      },
    ],
  });
};

export const checkoutStartedPlanUpgradeEvent = async ({
  artist,
  order,
  revenue,
}: {
  artist?: SimpleArtist;
  order: Order;
  revenue: string;
}) => {
  await checkoutStarted({
    created_by: 'Artist',
    artist_id: artist?.id,
    artist_name: artist?.name,
    checkout_source: 'plan upgrade',
    revenue: revenue,
    currency: order?.price.currency,
    products: [
      {
        name: 'Distribution Pro',
        category: 'Subscription',
        variant: 'yearly',
        product_id: order?.product.externalId,
        position: 1,
        price: order?.price.value,
      },
    ],
  });
};

export const orderCompletedMerchEvent = async ({
  order,
  artist,
  total,
  coupon,
}: {
  artist?: SimpleArtist;
  order: MerchSale;
  total?: number;
  coupon?: string;
}) => {
  return await orderCompleted({
    checkout_id: order.stripeOrderId,
    order_id: order?.id,
    total_price: total,
    shipping_cost: order.shippingPrice,
    total_tax: order.tax,
    coupon,
    currency: order.currency || 'USD',
    artist_tracpage_url: artist?.domain,
    artist_id: artist?.id,
    created_by: 'Artist',
    artist_name: artist?.name,
    products: order?.products?.map(
      (product): MerchProductOrderItem => ({
        category: 'Merch',
        purchase_type: 'One Time',
        length: product.quantity,
        product_id: product.id,
        variant_id: product.variantId,
        color: product.color?.name,
        size: product.size.name,
        item_type: product.sku,
        quantity: product.quantity,
        price: product.price,
        currency: 'USD',
      })
    ),
  });
};

export const orderCompletedReleaseEvent = async ({
  artist,
  total,
  result,
  releaseId,
  currency,
  coupon,
  tax,
}: {
  artist?: SimpleArtist;
  total?: number;
  result?: PaymentIntent;
  releaseId: string;
  currency?: string;
  coupon?: string;
  tax?: number;
}) => {
  await orderCompleted({
    checkout_id: result?.id,
    order_id: releaseId,
    total_price: total,
    total_tax: tax || 0,
    coupon,
    currency: currency || 'USD',
    artist_id: artist?.id,
    created_by: 'Artist',
    artist_name: artist?.name,
    artist_tracpage_url: artist?.domain,
    products: [
      {
        purchase_type: 'One Time',
        category: 'Release',
        length: 1,
        price: result?.amount,
        currency: 'USD',
      },
    ],
  });
};

export const orderCompletedSubscriptionEvent = async (
  action: CreateSubscriptionSuccess,
  subscription: Subscription
) => {
  await orderCompleted({
    checkout_id: subscription?.externalId,
    order_id: subscription?.id,
    total_price: subscription.price.value,
    total_tax: action.tax || 0,
    coupon: action.coupon,
    currency: action.order?.price.currency,
    artist_id: action.customer?.id,
    created_by: 'Artist',
    artist_name: action.customer?.name,
    products: [
      {
        product_id: subscription?.externalId,
        name: subscription.product.name,
        price: DineroFactory({
          currency: action.order?.price.currency || 'USD',
          amount: subscription.price.value || action.order?.price.value,
        }).toUnit(),
        quantity: action.order?.quantity,
      },
    ],
  });
};
